
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { loadWidget, truncateContent } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { Getter, namespace } from 'vuex-class';
import moment from 'moment';
import {
  IAPIKeys,
  IDashboardCount,
  ILogsCount,
  IScheduledEvent,
  ISenderID,
  IUser,
} from '@/types/types';
import { Watch } from 'vue-property-decorator';
const authModule = namespace('auth');
const apiKeyModule = namespace('apiKeys');
const reportsModule = namespace('reports');
const smsModule = namespace('sms');
const usersModule = namespace('users');
const eventsModule = namespace('events');
@Component({
  name: 'Dashboard',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    DoubleDatePicker: loadWidget('widgets/DoubleDatePicker'),
    AddSMSSenderID: loadWidget(`widgets/AddSenderID`),
  },
  filters: {
    timestamp(value: string, format?: string) {
      return moment(value).format(format ?? 'Do MMMM, YYYY');
    },
    truncateContent,
  },
})
export default class Dashboard extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @apiKeyModule.Getter('getAPIKeys') accessKeys!: IAPIKeys;
  @reportsModule.Getter('getDashboardCount') count!: IDashboardCount;
  @reportsModule.Getter('getDailyDeliveryStats') dailyReports!: Array<{
    key: string;
    logs: ILogsCount;
  }>;
  @smsModule.Getter('getSenderIDList') smsIDList!: Array<
    ISenderID & { uuid: string; id: number; slug: string }
  >;

  @authModule.Getter('getUserDetails') user!: IUser;
  @eventsModule.Getter('getAllEvents') events!: Array<
    IScheduledEvent & { start: string }
  >;
  @usersModule.Getter('getSearchResults') subAccounts!: IUser[];

  date = [];
  menu = false;
  query = '';

  picker = '';
  minDate = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartOptions: any = {
    chart: {
      type: 'area',
      height: 600,
    },
    colors: ['#f6b22b', '#01db01', '#484747', '#723A3A', '#e20639'],
    legend: {
      fontFamily: 'Outfit,sans-serif',
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      curve: 'smooth',
      width: 2,
      color: ['transparent'],
    },
    xaxis: {
      categories: [],
    },
    yaxis: {
      title: {
        text: 'Messages Count ',
        style: {
          fontSize: '12px',
          fontFamily: 'Outfit, sans-serif',
        },
      },
      min: 1,
    },
    fill: {
      opacity: 1,
    },
  };

  chartBarOptions: any = {
    chart: {
      type: 'bar',
      height: 350,
    },
    colors: ['rgba(2,103,36,0.92)'],
    legend: {
      fontFamily: 'Outfit,sans-serif',
    },
    grid: {
      show: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  series: Array<{ name: string; data: number[] }> = [];

  // top sender address graph
  topSenderAddressGraphCategories: string[] = [];
  topSenderAddressGraphSeries: Array<{ name: string; data: number[] }> = [];
  bottomSenderAddressGraphSeries: Array<{ name: string; data: number[] }> = [];
  bottomSenderAddressGraphCategories: string[] = [];

  eventsCalendar: string[] = [];
  upcomingEvents: Array<IScheduledEvent & { start: string }> = [];

  @Watch('events')
  onScheduledEventsChange(
    value: Array<IScheduledEvent & { start: string }>
  ): void {
    this.eventsCalendar = value.map((event) =>
      moment(event.start).format('YYYY-MM-DD')
    );
    this.upcomingEvents = value
      .filter((event) => event.state === 'SCHEDULED')
      .slice(0, 6);
  }

  @Watch('dailyReports')
  onDailyReportsChange(dailyReports: {
    deliveryReceiptGraph: Array<{ key: string; logs: ILogsCount }>;
    topSenderAddressesGraph: Array<{
      _id: string;
      totalSMSViaSenderAddress: number;
    }>;
    bottomSenderAddressesGraph: Array<{
      _id: string;
      totalSMSViaSenderAddress: number;
    }>;
  }): void {
    console.log(dailyReports);
    const categories = dailyReports.deliveryReceiptGraph.map(
      (report) => report.key
    );
    this.chartOptions = {
      ...this.chartOptions,
      xaxis: {
        categories:
          categories.length > 0
            ? categories
            : [
                ...new Array(7)
                  .fill(0)
                  .map((item, index) =>
                    moment().add(index, 'day').format('YYYY-MM-DD')
                  ),
              ],
      },
    };
    //this.series = []
    this.series = [
      {
        name: 'ACCEPTED',
        data: dailyReports.deliveryReceiptGraph.map(
          (report) => report.logs.accepted
        ),
      },
      {
        name: 'DELIVERED',
        data: dailyReports.deliveryReceiptGraph.map(
          (report) => report.logs.delivered
        ),
      },
      {
        name: 'UNDELIVERED',
        data: dailyReports.deliveryReceiptGraph.map(
          (report) => report.logs.undelivered
        ),
      },
      {
        name: 'EXPIRED',
        data: dailyReports.deliveryReceiptGraph.map(
          (report) => report.logs.expired
        ),
      },
      {
        name: 'REJECTED',
        data: dailyReports.deliveryReceiptGraph.map(
          (report) => report.logs.rejected
        ),
      },
    ];
    this.topSenderAddressGraphCategories =
      dailyReports.topSenderAddressesGraph.map((report) => report._id);
    this.topSenderAddressGraphSeries = [
      {
        name: 'Messages',
        data: dailyReports.topSenderAddressesGraph.map(
          (sender) => sender.totalSMSViaSenderAddress
        ),
      },
    ];
    this.bottomSenderAddressGraphCategories =
      dailyReports.bottomSenderAddressesGraph.map((report) => report._id);
    this.bottomSenderAddressGraphSeries = [
      {
        name: 'Messages',
        data: dailyReports.bottomSenderAddressesGraph.map(
          (sender) => sender.totalSMSViaSenderAddress
        ),
      },
    ];
  }

  @Watch('date')
  onDateChange(date: string): void {
    if (!date) this.$store.dispatch('reports/dailyDeliveryStats');
  }

  filterByDates(): void {
    (this.$refs.menu as HTMLElement & { save: (date: string[]) => void }).save(
      this.date
    );
    if (this.date.length > 0) {
      const [startDate, endDate] = this.date;
      this.query = `?startDate=${startDate}&endDate=${endDate}`;
      this.$store.dispatch('reports/dailyDeliveryStats', this.query);
      return;
    }
    this.query = '';
    this.$store.dispatch('reports/dailyDeliveryStats');
  }

  refreshPage(): void {
    this.$store.dispatch('reports/dailyDeliveryStats', this.query);
  }
  created(): void {
    this.minDate = moment().subtract(3, 'month').format('YYYY-MM-DD');
    this.$store.dispatch('reports/dashboardCount');
    this.$store.dispatch('reports/dailyDeliveryStats');
    this.picker = moment().format('YYYY-MM-DD');
    this.$store.dispatch('sms/listSenderID', { isApproved: false });
    this.$store.dispatch('events/list', {
      query: `?activeDate=${moment().format('YYYY-MM-DD')}&isPaginated=false`,
    });
    if (!this.subAccounts || this.subAccounts.length === 0) {
      this.$store.dispatch('users/rawList', {
        query: `?rawList=true`,
      });
    }
  }
}
